import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, MessageCircle, Menu, X } from "lucide-react";
import { CLINIC, PAGES } from "../lib/clinic";

const Monogram = ({ scrolled }) => (
  <span
    className={`relative inline-flex items-center justify-center w-11 h-11 border ${
      scrolled ? "border-brand-navyDark/60" : "border-brand-navyDark/40"
    } transition-colors`}
  >
    <span className="heading-serif text-brand-navyDark text-[20px] font-semibold tracking-[-0.04em] leading-none">
      AD
    </span>
  </span>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#FAF9F7]/92 backdrop-blur-xl border-b border-stone-200/70 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="container-x flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-4 group" data-testid="navbar-logo">
          <Monogram scrolled={scrolled} />
          <div className="leading-tight whitespace-nowrap hidden sm:block">
            <div className="heading-serif text-xl md:text-[22px] font-semibold text-brand-navyDark tracking-tight">
              Dr. Avril Dhar
            </div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-brand-goldDark font-medium mt-0.5">
              Neurologist · Psychologist
            </div>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-0.5">
          {PAGES.map((p) => (
            <NavLink
              key={p.path}
              to={p.path}
              data-testid={`nav-link-${p.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-3.5 py-2 text-[13px] font-medium tracking-wide whitespace-nowrap transition-colors ${
                  isActive
                    ? "text-brand-navyDark"
                    : "text-brand-navyDark/65 hover:text-brand-navyDark"
                }`
              }
              end={p.path === "/"}
            >
              {p.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2 shrink-0">
          <a
            href={`tel:${CLINIC.phoneRaw}`}
            className="btn-ghost whitespace-nowrap"
            data-testid="navbar-call-btn"
          >
            <Phone className="w-3.5 h-3.5" strokeWidth={1.5} />
            Call
          </a>
          <a
            href={CLINIC.whatsapp}
            target="_blank"
            rel="noreferrer"
            className="btn-primary whitespace-nowrap"
            data-testid="navbar-whatsapp-btn"
          >
            <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.5} />
            WhatsApp
          </a>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          className="xl:hidden w-11 h-11 border border-brand-navyDark/30 flex items-center justify-center text-brand-navyDark"
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="xl:hidden border-t border-stone-200 bg-[#FAF9F7]/98 backdrop-blur-xl"
            data-testid="mobile-menu"
          >
            <div className="container-x py-7 flex flex-col gap-1">
              {PAGES.map((p) => (
                <NavLink
                  key={p.path}
                  to={p.path}
                  end={p.path === "/"}
                  className={({ isActive }) =>
                    `px-4 py-3 text-[15px] tracking-wide ${
                      isActive ? "text-brand-navyDark font-medium" : "text-brand-navyDark/70"
                    }`
                  }
                  data-testid={`mobile-nav-${p.label.toLowerCase()}`}
                >
                  {p.label}
                </NavLink>
              ))}
              <div className="flex gap-2 mt-5">
                <a
                  href={`tel:${CLINIC.phoneRaw}`}
                  className="btn-secondary flex-1"
                  data-testid="mobile-call-btn"
                >
                  <Phone className="w-3.5 h-3.5" /> Call
                </a>
                <a
                  href={CLINIC.whatsapp}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-primary flex-1"
                  data-testid="mobile-whatsapp-btn"
                >
                  <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
